import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardGroup from '../CardGroup/CardGroup';
import MostPopularCard from '../MostPopularCard/MostPopularCard';
import CardOverlay from '../CardOverlay/CardOverlay';
import { useDiscoverSection } from '../../hooks/useDiscoverSection';
import { useMostPopularCard } from '../../hooks/useMostPopularCard';
import styles from './DiscoverSection.module.css';

const DiscoverSection = ({ 
    componentSize = "Desktop 1440"
}) => {
    const navigate = useNavigate();
    const [selectedCard, setSelectedCard] = useState(null);

    const {
        trendingCards,
        justInCards,
        isLoading,
        error
    } = useDiscoverSection();

    const { 
        cardData: mostPopularData,
        isLoading: isMostPopularLoading 
    } = useMostPopularCard();

    const handleMostPopularView = useCallback(() => {
        if (componentSize === "Phone") {
            mostPopularData?.id && setSelectedCard(mostPopularData.id);
        } else if (mostPopularData?.companyname) {
            navigate(`/company/${mostPopularData.companyname.toLowerCase().replace(/\s+/g, '_')}`);
        }
    }, [componentSize, mostPopularData, navigate]);

    const handleRedirect = useCallback((path) => {
        navigate(path);
    }, [navigate]);

    const handleOverlayClose = useCallback(() => {
        setSelectedCard(null);
    }, []);

    const getSectionWidth = useCallback((size) => {
        switch (size) {
            case "Phone":
                return 362.4;
            case "Tablet":
                return 600;
            case "Desktop 1200":
                return 1057;
            case "Desktop 1440":
            default:
                return 1210;
        }
    }, []);

    const renderSectionHeader = useCallback((title, buttonText, onButtonClick) => (
        <div
            className={styles.sectionHeader}
            style={{ width: getSectionWidth(componentSize) }}
        >
            <div className={styles.sectionTitle}>
                <span className={styles.sectionTitleText}>{title}</span>
            </div>
            <button className={styles.sectionButton} onClick={onButtonClick}>
                <span className={styles.sectionButtonText}>{buttonText}</span>
            </button>
        </div>
    ), [componentSize, getSectionWidth]);

    if (error) {
        return <div className={styles.error}>{error.message}</div>;
    }

    if (isLoading || isMostPopularLoading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.mainContainer}>
            <div className={styles.contentContainer}>
                {renderSectionHeader(
                    "Most popular project of the week",
                    "View",
                    handleMostPopularView
                )}
                
                {componentSize === "Phone" ? (
                    mostPopularData && (
                        <div
                            className={styles.singleCardContainer}
                            style={{ width: getSectionWidth(componentSize) }}
                        >
                            <CardGroup
                                size={componentSize}
                                cardCount={1}
                                groupPick12={1}
                                groupPick7={1}
                                cardId1={mostPopularData.id}
                            />
                        </div>
                    )
                ) : (
                    <div className={styles.mostPopularContainer}>
                        <MostPopularCard
                            size={componentSize}
                            onViewMoreClick={handleMostPopularView}
                        />
                    </div>
                )}

                {renderSectionHeader(
                    "Also popular projects on Waitlist",
                    "See all",
                    () => handleRedirect("/trending/all")
                )}
                
                {trendingCards.length > 0 && (
                    <CardGroup
                        size={componentSize}
                        cardCount={5}
                        groupPick12={1}
                        groupPick7={1}
                        // usamos los datos ya filtrados por el hook (sin la más popular)
                        cardId1={trendingCards[0]?.id || ""}
                        cardId2={trendingCards[1]?.id || ""}
                        cardId3={trendingCards[2]?.id || ""}
                        cardId4={trendingCards[3]?.id || ""}
                        cardId5={trendingCards[4]?.id || ""}
                    />
                )}

                {renderSectionHeader(
                    "Latest projects added to Waitlist",
                    "See All",
                    () => handleRedirect("/just_in/all")
                )}
                
                {justInCards.length > 0 && (
                    <CardGroup
                        size={componentSize}
                        cardCount={5}
                        groupPick12={2}
                        groupPick7={2}
                        cardId1={justInCards[0]?.id || ""}
                        cardId2={justInCards[1]?.id || ""}
                        cardId3={justInCards[2]?.id || ""}
                        cardId4={justInCards[3]?.id || ""}
                        cardId5={justInCards[4]?.id || ""}
                    />
                )}
            </div>

            {selectedCard && (
                <CardOverlay
                    cardId={selectedCard}
                    onClose={handleOverlayClose}
                />
            )}
        </div>
    );
};

export default DiscoverSection;